// Direct import of selector module needed instead of useBrand hook in selector context where hooks cannot be used.
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import {ClientOptions} from '@findhotel/sapi'
import {createSelector, createStructuredSelector} from 'reselect'
import {RootState} from 'store'
import {MeasurementSystemType} from 'types/Localization'
import {MetaState} from 'types/State'

import {
  getBrand,
  getBrandSapiCode
} from '@vio/core/src/_web/brand/modules/selectors'
import {Page} from '@vio/core/src/analytics/types/Events'
import {getMyBookingsUrl as buildMyBookingsUrl} from '@vio/core/src/brand/utils'
import {safeDecode} from '@vio/core/src/utils/url'
import {DEVICE_TYPES} from '@vio/core/src/utils/userAgent/types'

import type {DevicePlatform} from '@vio/core/src/_web/utils/userAgent/types'
import type {DeviceCategory} from '@vio/core/src/utils/userAgent/types'

export type StateType = RootState

export const getMeta = (state: RootState): MetaState => state.meta

export const getIsRtlLanguage = (state: StateType): boolean =>
  Boolean(state.meta.isRTLLanguage)

export const getAnonymousId = createSelector(
  [(state: StateType) => state.meta.anonymousId],
  anonymousId => anonymousId ?? null
)

export const getLocaleCode = createSelector(
  [(state: StateType) => state.meta.localeCode],
  localeCode => localeCode
)

export const getLanguageCode = createSelector(
  [(state: StateType) => state.meta.languageCode],
  languageCode => languageCode ?? null
)

export const getSeoActiveLocales = createSelector(
  [(state: StateType) => state.meta.seoActiveLocales],
  seoActiveLocales => seoActiveLocales
)

export const getSeoActiveAttributes = createSelector(
  [(state: StateType) => state.meta.seoAttributes],
  seoAttributes => seoAttributes
)

export const getIsAcquiringGeoCoordinates = (state: StateType): boolean =>
  state.meta.isAcquiringGeoCoordinates ?? false

const getUserCountryCodeValue = (state: StateType): string =>
  state.meta.userCountryCode

export const getUserCountryCode = createSelector(
  [getUserCountryCodeValue],
  userCountryCode => userCountryCode
)

export const getIsIsraeliResident = createSelector(
  [getUserCountryCode],
  userCountryCode => userCountryCode === 'IL'
)

export const getUserCountryRegion = (state: StateType) =>
  state.meta.userCountryRegion

export const getUserCity = createSelector(
  [(state: StateType) => state.meta.userCity],
  userCity => (userCity ? safeDecode(userCity) : undefined)
)

export const getCurrencyCode = (state: StateType) =>
  state.meta.currencyCode || null

export const getDeviceCategory = (state: StateType): DeviceCategory =>
  state.meta.deviceCategory || 'desktop'

export const getDeviceLayout = (state: StateType): DeviceCategory =>
  state.meta.deviceLayout || 'desktop'

export const getDevicePlatform = (state: StateType): DevicePlatform =>
  state.meta.devicePlatform || null

export const getUserAgentDetails = ({
  meta
}: StateType): MetaState['userAgentDetails'] => meta.userAgentDetails

export const getUserAgent = ({meta}: StateType): MetaState['userAgent'] =>
  meta.userAgent

export const getTaxesIncluded = createSelector(
  [({meta}) => meta.includeTaxes],
  includeTaxes => includeTaxes
)

export const getLocalTaxesIncluded = createSelector(
  [({meta}) => meta.includeLocalTaxes],
  includeLocalTaxes => includeLocalTaxes
)

export const getAllTaxesIncluded = createSelector(
  [getTaxesIncluded, getLocalTaxesIncluded],
  (taxesAreIncluded, localTaxesAreIncluded) =>
    taxesAreIncluded && localTaxesAreIncluded
)

export const getTaxesOrFeesExcluded = createSelector(
  [getTaxesIncluded, getLocalTaxesIncluded],
  (taxesAreIncluded, localTaxesAreIncluded) =>
    taxesAreIncluded === false || localTaxesAreIncluded === false
)

export const getFallbackLanguages = createSelector(
  [(state: StateType) => state.meta.fallbackLanguages],
  fallbackLanguages => fallbackLanguages
)

export const getViewPortHeight = (state: StateType): number =>
  state.meta.viewPortHeight

export const getViewPortWidth = (state: StateType): number =>
  state.meta.viewPortWidth

export const getAnalyticsPageName = (state: StateType): Page => {
  return state.meta.analyticsPageName
}

export const getLocalizationIsInitialized = (state: StateType): boolean =>
  state.meta.localizationIsInitialized

export const getGeolocationCoordinates = (state: StateType) =>
  state.meta?.geolocation

export const getIsSyntheticTest = (state: StateType): boolean =>
  state.meta.anonymousId?.startsWith('test_website_synthetics') ?? false

export const getUserMeasurementSystem = (
  state: StateType
): MeasurementSystemType => state.meta.userMeasurementSystem

export const getUserCountryFromQueryString = (
  state: StateType
): string | undefined => {
  const searchParams = new URLSearchParams(state.search.landingQueryString)
  return searchParams.get('userCountry')?.toUpperCase() || undefined
}
const getDeviceTypeFromQueryString = (state: StateType): DeviceCategory => {
  const searchParams = new URLSearchParams(state.search.landingQueryString)
  return (searchParams.get('deviceType') as DeviceCategory) || undefined
}

type SapiMetaOptions = Pick<
  ClientOptions,
  | 'anonymousId'
  | 'language'
  | 'currency'
  | 'brand'
  | 'userCountry'
  | 'deviceType'
>

export const getDeviceType = createSelector(
  [
    getDeviceCategory,
    (state: StateType) => new URLSearchParams(state.search.queryString)
  ],
  (deviceCategory, searchParams) => {
    const deviceType = searchParams.get('deviceType') as DeviceCategory
    return DEVICE_TYPES.includes(deviceType) ? deviceType : deviceCategory
  }
)

export const getSapiMetaOptions = createStructuredSelector<
  RootState,
  SapiMetaOptions
>({
  anonymousId: getAnonymousId,
  language: getLanguageCode,
  currency: getCurrencyCode,
  brand: getBrandSapiCode,
  // we only want to send userCountry and deviceType value from query string (if it exists) and not from the state
  // as SAPI relies on Cloudfront headers to determine the userCountry and deviceType if it is not provided
  userCountry: getUserCountryFromQueryString,
  deviceType: getDeviceTypeFromQueryString
})

export const getSapiMetaOptionsForSSR = createSelector(
  [getSapiMetaOptions, getUserCountryCode, getDeviceType],
  (sapiMetaOptions, userCountry, deviceType): SapiMetaOptions => {
    const options: SapiMetaOptions = {...sapiMetaOptions}

    // When running on the backend, we need to send userCountry and deviceType as SAPI
    // will not be able to infer them from the Cloudfront headers
    // However, if they were already provided in the URL, we don't want to override them
    if (!options.userCountry) {
      options.userCountry = userCountry
    }
    if (!options.deviceType) {
      options.deviceType = deviceType
    }
    return options
  }
)

export const getMyBookingsUrl = createSelector(
  [
    getAnonymousId,
    getUserCountryCode,
    getLanguageCode,
    getCurrencyCode,
    getBrand
  ],
  (anonymousId, userCountry, locale, currency, brand) =>
    buildMyBookingsUrl({
      brand,
      appEnv: process.env.NEXT_PUBLIC_ENV!,
      anonymousId: anonymousId ?? undefined,
      userCountry,
      locale,
      currency
    })
)

const getIsReturningUserValue = (state: StateType): boolean =>
  state.meta.isReturningUser

export const getIsReturningUser = createSelector(
  [getIsReturningUserValue],
  isReturningUser => isReturningUser
)

export const getFirstVisit = (state: StateType): string => state.meta.firstVisit

export const getVisitsCount = (state: StateType): number =>
  state.meta.visitsCount

export const getDeviceId = createSelector(
  [(state: StateType): string => state.meta.deviceId],
  deviceId => deviceId
)

export const getTrafficSourceFromState = (state: StateType) =>
  state.meta.trafficSource

export const getUtmReferrerFromState = (state: StateType) =>
  state.meta.utmReferrer

export const getCurrencyExchangeRate = (state: StateType) =>
  state.meta.currencyExchangeRate
export const getCurrencyExchangeRateEur = (state: StateType) =>
  state.meta.currencyExchangeRateEur

export const getIsVioFlutterApp = (state: StateType): boolean =>
  state.meta.isVioFlutter

export const getIsEnglishLanguage = createSelector(
  [getLanguageCode],
  languageCode => languageCode.startsWith('en')
)
